<template>
    <div class="cf-c-attach-file-wrapper">
        <div class="cf-u-display-flex cf-u-flex-wrap cf-u-mb-sm">
            <file-item
                v-for="(file, index) in files"
                :key="index"
                :name="file.name"
                :path="file.path"
                class="cf-u-mx-xs cf-u-mb-sm"
                :removable="!disabled"
                @file-deleted="handleDelete(index)"
            />
        </div>

        <label v-if="!disabled" class="cf-c-attach-file">
            <input
                ref="filesRef"
                type="file"
                multiple
                class="cf-c-add-file__input"
                :accept="accept"
                @change="handleChange"
            />
            <div class="cf-c-attach-file__ctn">
                <app-icon icon="attachment-icon" class="cf-u-mr-xs" />
                <div
                    class="cf-u-width-min-width-5 cf-u-text-center cf-u-font-size-xs"
                >
                    {{ $t('COMMON.ATTACH_A_FILE') }}
                </div>
            </div>
        </label>

        <p
            v-if="!disabled && rulesHint"
            class="cf-u-mt-xs cf-u-font-size-xs cf-u-color-gray-03"
        >
            {{ rulesHint }}
        </p>
    </div>
</template>

<script>
import { findIndex, forEach, get } from 'lodash-es'
import { mapGetters } from 'vuex'
import { toasts } from '@/mixins'
import { UPLOAD_MAX_FILES_SIZE, TOAST_TYPES } from '@/constants'

export default {
    mixins: [toasts],

    props: {
        accept: {
            type: String,
            required: false,
            default: ''
        },

        files: {
            type: Array,
            required: false,
            default: () => []
        },

        disabled: {
            type: Boolean,
            required: false,
            default: false
        }
    },

    computed: {
        ...mapGetters('company', ['company']),

        // CT-574: published by CompanyResource, so what we tell the user is the same list the
        // validator is built from. Shown only -- deliberately never used to refuse a file, see
        // rejectionReason(). Empty until the company payload has loaded, in which case the hint
        // simply does not render.
        allowedExtensions () {
            return get(this.company, 'attachment_rules.allowed_extensions', [])
        },

        maxFileSizeMb () {
            return get(this.company, 'attachment_rules.max_filesize_mb', 0)
        },

        rulesHint () {
            if (!this.allowedExtensions.length || !this.maxFileSizeMb) {
                return ''
            }

            return this.$t('COMMON.ATTACHMENT_RULES_HINT', {
                extensions: this.allowedExtensions.join(', '),
                max: this.maxFileSizeMb
            })
        },

        // `size` exists only on files the user just picked -- the ones already saved come back from
        // the API without it. Before CT-574 that made the sum NaN, every comparison false, and this
        // guard dead as soon as a single attachment was saved.
        uploadSize () {
            return this.files.reduce((size, file) => size + (file.size || 0), 0)
        },

        maxUploadSize () {
            return UPLOAD_MAX_FILES_SIZE * 1000000
        }
    },

    methods: {
        handleChange () {
            const rejections = []
            let accepted = 0

            forEach(this.$refs.filesRef.files, fileRef => {
                const { name, size } = fileRef
                const index = findIndex(this.files, { name, size })

                if (index !== -1) {
                    return
                }

                // CT-574: reject before the file is added, not after the server refuses it. The old
                // order pushed first and never took anything back, so a refused file stayed on
                // screen looking attached and was quietly lost when the audit was completed.
                const rejection = this.rejectionReason(fileRef)

                if (rejection) {
                    rejections.push(rejection)
                    return
                }

                this.files.push(fileRef)
                accepted++
            })

            // Let the same file be picked again after a rejection: without this the input keeps its
            // value and re-selecting an identical file fires no change event.
            this.$refs.filesRef.value = ''

            rejections.forEach(rejection =>
                this.showToast(rejection, TOAST_TYPES.ERROR)
            )

            // One bad file used to fail the whole request and take the good ones with it. Upload
            // whatever passed, and stay silent when nothing did.
            if (accepted) {
                this.$emit('file-uploaded', this.files)
            }
        },

        /**
         * Why this file cannot be attached, or an empty string if it can.
         *
         * Size only, on purpose. File type is left entirely to the server, because the browser
         * cannot reproduce the judgement: `mimes` ignores the filename and validates the extension
         * PHP's fileinfo guesses from the file's *content*. An ordinary .csv is read as text/plain,
         * guesses back to `txt` and is accepted -- as is any plain-text file whatever it is called.
         * Refusing by filename would lock users out of files that upload perfectly well, and no
         * list of exceptions can close that gap, because the two ends are answering different
         * questions. A rejected type is still not lost: the error is shown and the list rolls back.
         *
         * Size is different -- it is the same number on both sides, so checking it here is exact.
         */
        rejectionReason (file) {
            if (
                this.maxFileSizeMb &&
                file.size > this.maxFileSizeMb * 1024 * 1024
            ) {
                return this.$t('COMMON.FILE_TOO_LARGE', {
                    name: file.name,
                    max: this.maxFileSizeMb
                })
            }

            if (this.uploadSize + file.size > this.maxUploadSize) {
                return this.$t('COMMON.MAX_UPLOAD_SIZE_EXCEEDED')
            }

            return ''
        },

        handleDelete (index) {
            this.files.splice(index, 1)
            this.$emit('file-uploaded', this.files)
        }
    }
}
</script>
